import React, { useState, useEffect, useRef } from 'react';
import { getUsers, createUser, deleteUser, adminChangePassword, adminEditUser, changeUserRole, updateUserPermissions } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Modal from '../../components/Modal';
import Alert from '../../components/Alert';
import Loading from '../../components/Loading';
import {
  PlusIcon,
  TrashIcon,
  UserIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  KeyIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';

export default function UsersPage() {
  const PAGE_SIZE = 25;

  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(null);
  const [showEdit, setShowEdit] = useState(null);
  const [editForm, setEditForm] = useState({ username: '', displayName: '', role: 'owner' });
  const [editPermForm, setEditPermForm] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ username: '', password: '', displayName: '', role: 'owner' });
  const [search, setSearch] = useState('');
  const [filterClub, setFilterClub] = useState('');
  const [allClubs, setAllClubs] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, hasPrev: false, hasNext: false });
  const [sortBy, setSortBy] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search]);

  useEffect(() => {
    loadUsers();
  }, [page, debouncedSearch, filterClub, sortBy]);

  const loadUsers = async () => {
    if (loading) {
      // initial load — show full spinner
    } else {
      setSearching(true);
    }
    try {
      const res = await getUsers({ page, limit: PAGE_SIZE, search: debouncedSearch, club: filterClub, role: sortBy });
      let loadedUsers;
      if (Array.isArray(res.data)) {
        loadedUsers = res.data;
        setAllClubs([...new Set(res.data.flatMap(u => (u.organizations || []).map(o => o.name)))].sort());
        setPagination({ page: 1, totalPages: 1, total: res.data.length, hasPrev: false, hasNext: false });
        if (page !== 1) setPage(1);
      } else {
        loadedUsers = res.data.items || [];
        setAllClubs(res.data.clubs || []);
        const nextPagination = res.data.pagination || { page: 1, totalPages: 1, total: 0, hasPrev: false, hasNext: false };
        setPagination(nextPagination);
        if (nextPagination.page !== page) setPage(nextPagination.page);
      }

      setUsers(loadedUsers);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await createUser(form);
      await loadUsers();
      setShowCreate(false);
      setForm({ username: '', password: '', displayName: '', role: 'owner' });
      setSuccess('User created successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await deleteUser(userId);
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword.trim() || !showPasswordChange) return;
    setError('');
    try {
      await adminChangePassword(showPasswordChange._id, newPassword);
      setShowPasswordChange(null);
      setNewPassword('');
      setSuccess(`Password changed for ${showPasswordChange.displayName}!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    }
  };

  const openEdit = (u) => {
    setEditForm({ username: u.username, displayName: u.displayName, role: u.role });
    setEditPermForm(u.permissions || {});
    setShowEdit(u);
  };

  const handleEdit = async () => {
    if (!showEdit) return;
    setError('');
    try {
      await adminEditUser(showEdit._id, { username: editForm.username, displayName: editForm.displayName });
      if (editForm.role !== showEdit.role) {
        await changeUserRole(showEdit._id, editForm.role);
      }
      await updateUserPermissions(showEdit._id, editPermForm);
      await loadUsers();
      setShowEdit(null);
      setSuccess('User updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <ShieldCheckIcon className="icon" />;
      case 'owner': return <UserIcon className="icon" />;
      case 'leader': return <ShieldCheckIcon className="icon" />;
      default: return <UserGroupIcon className="icon" />;
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin': return 'badge-purple';
      case 'owner': return 'badge-info';
      case 'leader': return 'badge-warning';
      default: return 'badge-success';
    }
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>User Management</h2>
          <div className="subtitle">Manage all ClubMaster users</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <PlusIcon className="icon" /> Create Owner
        </button>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Search & Filter */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <MagnifyingGlassIcon style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, color: 'var(--text-muted)' }} />
          <input
            className="form-input"
            placeholder="Search by name or username..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 36 }}
          />
        </div>
        <select className="form-input" value={filterClub} onChange={e => { setFilterClub(e.target.value); setPage(1); }} style={{ width: 'auto', minWidth: 180 }}>
          <option value="">All Clubs</option>
          {allClubs.map(club => (
            <option key={club} value={club}>{club}</option>
          ))}
        </select>
        <select className="form-input" value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }} style={{ width: 'auto', minWidth: 160 }}>
          <option value="">All roles</option>
          <option value="owner">Owner</option>
          <option value="leader">Leader</option>
          <option value="member">Member</option>
        </select>
      </div>

      <div className="members-list" style={{ opacity: searching ? 0.6 : 1, transition: 'opacity 0.15s' }}>
        {users.map(u => (
          <div key={u._id} className="member-item">
            <div className="member-info">
              <div className="member-avatar" style={{
                background: u.role === 'admin' ? 'var(--accent)' : u.role === 'owner' ? 'var(--info)' : u.role === 'leader' ? 'var(--warning)' : 'var(--success)'
              }}>
                {u.displayName?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <div className="member-name">{u.displayName}</div>
                <div className="member-role">@{u.username}</div>
                {(u.organizations || []).length > 0 && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                    {u.organizations.map(o => (
                      <span
                        key={o._id}
                        onClick={(e) => { e.stopPropagation(); navigate(`/org/${o._id}`); }}
                        style={{ fontSize: 11, padding: '2px 8px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 12, display: 'inline-flex', alignItems: 'center', gap: 3, cursor: 'pointer', transition: 'opacity 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                      >
                        {o.logo ? (
                          <img src={o.logo} alt="" style={{ width: 14, height: 14, borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <BuildingOfficeIcon style={{ width: 12, height: 12 }} />
                        )}
                        {o.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span className={`badge ${getRoleBadge(u.role)}`}>
                {getRoleIcon(u.role)} {u.role}
              </span>
              {u._id !== user._id && (
                <>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)} title="Edit user" style={{ color: 'var(--accent)' }}>
                    <PencilSquareIcon className="icon" />
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setShowPasswordChange(u); setNewPassword(''); }} title="Change password" style={{ color: 'var(--info)' }}>
                    <KeyIcon className="icon" />
                  </button>
                </>
              )}
              {u.username !== 'admin' && u._id !== user._id && (
                <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(u._id)} style={{ color: 'var(--danger)' }}>
                  <TrashIcon className="icon" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Total users: <strong>{pagination.total}</strong>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" disabled={!pagination.hasPrev} onClick={() => setPage(prev => Math.max(prev - 1, 1))}>
            Previous
          </button>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Page <strong>{pagination.page}</strong> / <strong>{pagination.totalPages}</strong>
          </span>
          <button className="btn btn-secondary btn-sm" disabled={!pagination.hasNext} onClick={() => setPage(prev => prev + 1)}>
            Next
          </button>
        </div>
      </div>

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Create Organization Owner"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleCreate}>Create User</button>
          </>
        }
      >
        <form onSubmit={handleCreate}>
          <div className="form-group">
            <label>Username</label>
          <input className="form-input" value={form.username} onChange={e => setForm({...form, username: e.target.value.slice(0, 30)})} placeholder="e.g. johndoe" maxLength={30} required />
          <div style={{ fontSize: 11, color: form.username.length >= 30 ? 'var(--danger)' : 'var(--text-muted)', marginTop: 4 }}>{form.username.length}/30 characters</div>
          </div>
          <div className="form-group">
            <label>Display Name</label>
          <input className="form-input" value={form.displayName} onChange={e => setForm({...form, displayName: e.target.value.slice(0, 30)})} placeholder="e.g. John Doe" maxLength={30} required />
          <div style={{ fontSize: 11, color: form.displayName.length >= 30 ? 'var(--danger)' : 'var(--text-muted)', marginTop: 4 }}>{form.displayName.length}/30 characters</div>
          </div>
          <div className="form-group">
            <label>Password</label>
            <input className="form-input" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Choose a strong password" required />
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--info-light)', borderRadius: 'var(--radius)' }}>
            This user will be created as an <strong>Organization Owner</strong> and will be able to create their own club and manage members.
          </div>
        </form>
      </Modal>

      {/* Edit User Modal */}
      <Modal isOpen={!!showEdit} onClose={() => setShowEdit(null)} title={`Edit User: ${showEdit?.displayName || ''}`}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowEdit(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleEdit}>Save Changes</button>
          </>
        }
      >
        <div className="form-group">
          <label>Username</label>
          <input className="form-input" value={editForm.username} onChange={e => setEditForm({...editForm, username: e.target.value.slice(0, 30)})} maxLength={30} />
          <div style={{ fontSize: 11, color: editForm.username.length >= 30 ? 'var(--danger)' : 'var(--text-muted)', marginTop: 4 }}>{editForm.username.length}/30</div>
        </div>
        <div className="form-group">
          <label>Display Name</label>
          <input className="form-input" value={editForm.displayName} onChange={e => setEditForm({...editForm, displayName: e.target.value.slice(0, 30)})} maxLength={30} />
          <div style={{ fontSize: 11, color: editForm.displayName.length >= 30 ? 'var(--danger)' : 'var(--text-muted)', marginTop: 4 }}>{editForm.displayName.length}/30</div>
        </div>
        <div className="form-group">
          <label>Role</label>
          <select className="form-input" value={editForm.role} onChange={e => setEditForm({...editForm, role: e.target.value})}>
            <option value="member">Member</option>
            <option value="leader">Leader</option>
            <option value="owner">Owner</option>
            <option value="admin">Admin</option>
          </select>
        </div>
          <div className="form-group">
          <label style={{ marginBottom: 8, display: 'block' }}>Permissions</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { key: 'manageBoards', label: 'Kanban Board Access' },
              { key: 'manageMembers', label: 'Manage Members' },
              { key: 'manageCalendar', label: 'Manage Calendar' },
              { key: 'manageEvents', label: 'Manage Events' },
              { key: 'manageNotes', label: 'Member Notes' },
              { key: 'manageOutfit', label: 'Edit Outfit' },
              { key: 'manageDiscord', label: 'Manage Discord' },
            ].map(perm => (
              <label key={perm.key} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 12px', background: 'var(--bg-primary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                <input
                  type="checkbox"
                  checked={!!editPermForm[perm.key]}
                  onChange={e => setEditPermForm(prev => ({ ...prev, [perm.key]: e.target.checked }))}
                />
                <span style={{ fontSize: 14 }}>{perm.label}</span>
              </label>
            ))}
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 12px', background: 'var(--bg-primary)', borderRadius: 'var(--radius)', border: '1px solid #7c3aed' }}>
              <input
                type="checkbox"
                checked={!!editPermForm.adminDiscord}
                onChange={e => setEditPermForm(prev => ({ ...prev, adminDiscord: e.target.checked }))}
              />
              <span style={{ fontSize: 14 }}>Admin Discord Access</span>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: '#7c3aed', fontWeight: 600 }}>ADMIN ONLY</span>
            </label>
          </div>
        </div>
      </Modal>

      {/* Change Password Modal */}
      <Modal isOpen={!!showPasswordChange} onClose={() => setShowPasswordChange(null)} title={`Change Password: ${showPasswordChange?.displayName || ''}`}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowPasswordChange(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleChangePassword}>Change Password</button>
          </>
        }
      >
        <div className="form-group">
          <label>New Password</label>
          <input
            className="form-input"
            type="password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            autoFocus
          />
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          This will immediately change the password for <strong>{showPasswordChange?.displayName}</strong> (@{showPasswordChange?.username}).
        </div>
      </Modal>
    </div>
  );
}
